/**
 * SnapProvider - Canvas-level context for alignment snapping
 *
 * Provides snap points from all blocks on the canvas and tracks active guides.
 * Used by Draggable to snap blocks to alignment positions during drag.
 */

import { createContext, useContext, useMemo, useState, type ReactNode } from 'react'
import type { PlayBlock_Union } from '@/types/blocks'
import { extractSpatialProperties, isSpatialBlock } from '@/utils/Block.utils'

export interface SnapPoint {
	position: number // Percentage (0-100)
	axis: 'x' | 'y'
	blockId: string
	edge: 'start' | 'center' | 'end'
}

interface SnapContextValue {
	snapPoints: SnapPoint[]
	activeGuides: SnapPoint[]
	setActiveGuides: (guides: SnapPoint[]) => void
	findSnapPoint: (position: number, axis: 'x' | 'y', threshold?: number) => SnapPoint | null
}

const SnapContext = createContext<SnapContextValue | null>(null)

interface SnapProviderProps {
	blocks: PlayBlock_Union[]
	children: ReactNode
}

export function SnapProvider({ blocks, children }: SnapProviderProps) {
	const [activeGuides, setActiveGuides] = useState<SnapPoint[]>([])

	const snapPoints = useMemo(() => {
		const points: SnapPoint[] = []

		blocks.forEach((block) => {
			// Only snap to spatial blocks (those with x,y position properties)
			if (!isSpatialBlock(block)) {
				return
			}

			const spatial = extractSpatialProperties(block)
			if (!spatial) {
				return
			}

			const x = spatial.x
			const y = spatial.y
			// Width/height are optional in some block property types, default to a sensible snap value
			// Use 'in' check for type-safe access to optional properties in union types
			const props = block.properties as Record<string, unknown>
			const width = (typeof props.width === 'number' ? props.width : undefined) ?? 10
			const height = (typeof props.height === 'number' ? props.height : undefined) ?? 10
			const { id } = block

			// X axis: left, center, right edges
			points.push({ position: x, axis: 'x', blockId: String(id), edge: 'start' })
			points.push({ position: x + width / 2, axis: 'x', blockId: String(id), edge: 'center' })
			points.push({ position: x + width, axis: 'x', blockId: String(id), edge: 'end' })

			// Y axis: top, center, bottom edges
			points.push({ position: y, axis: 'y', blockId: String(id), edge: 'start' })
			points.push({ position: y + height / 2, axis: 'y', blockId: String(id), edge: 'center' })
			points.push({ position: y + height, axis: 'y', blockId: String(id), edge: 'end' })
		})

		return points
	}, [blocks])

	const findSnapPoint = (position: number, axis: 'x' | 'y', threshold = 2) => {
		return (
			snapPoints
				.filter((p) => p.axis === axis)
				.find((p) => Math.abs(p.position - position) < threshold) ?? null
		)
	}

	return (
		<SnapContext.Provider value={{ snapPoints, activeGuides, setActiveGuides, findSnapPoint }}>
			{children}
		</SnapContext.Provider>
	)
}

export function useSnap() {
	const context = useContext(SnapContext)
	if (!context) throw new Error('useSnap must be used within SnapProvider')
	return context
}
